import EventEmitter from "events";
import React, { SetStateAction, useCallback, useEffect, useState } from "react";

const trigger = new EventEmitter();

const storagePrefix = "react:persistence";

type Bucket = {
    data: any,
    guard?: string;
};

type SessionStorage = typeof sessionStorage;
type LocalStorage = typeof localStorage;
type StorageType = SessionStorage | LocalStorage;

const retrieve = (key: string, storage: StorageType = sessionStorage): Bucket | null =>
{
    const cache = storage.getItem(`${storagePrefix}:${key}`);
    return cache === null ? null : JSON.parse(cache);
};

const store = (key: string, data: Bucket, storage: StorageType = sessionStorage) =>
{
    storage.setItem(`${storagePrefix}:${key}`, JSON.stringify(data));
    trigger.emit(key, data.data);
    return data;
};

export const usePersistentState = <T extends any>(
    persistenceKey: string,
    defaultValue?: T,
    resetGuard?: string,
    storage: StorageType = sessionStorage
): [T, React.Dispatch<React.SetStateAction<T>>] =>
{
    let bucket = retrieve(persistenceKey, storage);

    if (bucket === null || (resetGuard && bucket.guard !== resetGuard)) {
        bucket = store(persistenceKey, {
            data: defaultValue,
            guard: resetGuard
        });
    }

    const [data, setData] = useState<T>(bucket.data);

    const persistData: React.Dispatch<React.SetStateAction<T>> = useCallback((update: SetStateAction<T>) =>
    {
        if (typeof update === 'function') {
            // complicated case: we don`t know the new state yet BUT we know that react will call any function passed to setState at the right time
            // so we just create this function here
            const wrappedUpdate: SetStateAction<T> = (newData: T) =>
            {
                // call the original update function to produce the state that we want to persist
                const evenNewerData = (update as ((prevState: T) => T))(newData);
                store(persistenceKey, {
                    data: evenNewerData,
                    guard: resetGuard
                }, storage);
                // return the new data for setState
                return evenNewerData;

            };
            setData(wrappedUpdate);
            return;
        }

        // simple case: update is just the new data object so we
        // just persist it
        store(persistenceKey, {
            data: update,
            guard: resetGuard
        }, storage);
        // and let setState do it's job
        setData(update);

    }, [persistenceKey, resetGuard, storage]);

    useEffect(() =>
    {
        const forward = (data: T) => setTimeout(() =>
        {
            setData(data);
        });

        trigger.on(persistenceKey, forward);

        return () =>
        {
            trigger.off(persistenceKey, forward);
        };
    }, [persistenceKey]);

    return [data, persistData];

};

export
{
    retrieve as retrievePersistentData
};
